'use client';

import { useState } from 'react';
import { getShareUrl, appendReferralParam } from '@/lib/referral-url';

interface AdmissionShareButtonsProps {
  pathway: string;
  year: number;
  lang: string;
  nextDeadline?: string;
}

export function AdmissionShareButtons({ pathway, year, lang, nextDeadline }: AdmissionShareButtonsProps) {
  const [copied, setCopied] = useState(false);
  const isId = lang === 'id';

  const handleWhatsApp = () => {
    const url = getShareUrl();
    const deadlineInfo = nextDeadline ? `\n${nextDeadline}` : '';
    const text = isId
      ? `📅 ${pathway} ${year} — Jadwal & Info Lengkap${deadlineInfo}\n\nLihat jadwal lengkap: ${url}`
      : `📅 ${pathway} ${year} — Complete Timeline${deadlineInfo}\n\nSee full schedule: ${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    window._paq?.push(['trackEvent', 'Share', 'WhatsApp-Parent', pathway]);
  };

  const handleCopy = async () => {
    try {
      const url = getShareUrl();
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      window._paq?.push(['trackEvent', 'Share', 'CopyLink', pathway]);
    } catch {
      // Fallback for older browsers
    }
  };

  const handleWhatsAppClassmate = () => {
    const url = getShareUrl();
    const deadlineInfo = nextDeadline ? `\n📌 ${nextDeadline}` : '';
    const text = isId
      ? `Teman-teman, ini info lengkap ${pathway} ${year}! 📅${deadlineInfo}\n\nCek jadwal & cara daftar: ${url}`
      : `Hey everyone, here's the complete ${pathway} ${year} info! 📅${deadlineInfo}\n\nCheck schedule & how to register: ${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    window._paq?.push(['trackEvent', 'Share', 'WhatsApp-Classmate', pathway]);
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <button
        onClick={handleWhatsAppClassmate}
        className="inline-flex items-center gap-1.5 rounded-full bg-semantic-success/10 px-3 py-2 text-xs font-semibold text-semantic-success transition-colors hover:bg-semantic-success/20"
        aria-label={isId ? 'Bagikan ke teman sekelas via WhatsApp' : 'Share with classmates via WhatsApp'}
      >
        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
        {isId ? 'Bagikan ke Teman Sekelas' : 'Share with Classmates'}
      </button>
      <button
        onClick={handleWhatsApp}
        className="inline-flex items-center gap-1.5 rounded-full bg-surface-card px-3 py-1.5 text-xs font-medium text-text-secondary transition-colors hover:bg-surface-hover"
        aria-label={isId ? 'Kirim ke orang tua via WhatsApp' : 'Share with parent via WhatsApp'}
      >
        <svg className="h-3.5 w-3.5 text-semantic-success" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
        {isId ? 'Kirim ke Orang Tua' : 'Share with Parent'}
      </button>
      <button
        onClick={handleCopy}
        className="inline-flex items-center gap-1.5 rounded-full bg-surface-card px-3 py-1.5 text-xs font-medium text-text-secondary transition-colors hover:bg-surface-hover"
        aria-label={isId ? 'Salin tautan' : 'Copy link'}
      >
        {copied
          ? (isId ? '✓ Tersalin' : '✓ Copied')
          : (isId ? 'Salin Tautan' : 'Copy Link')}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Parent-specific WhatsApp share button for lolos-SNBP university cards
// ---------------------------------------------------------------------------

interface ParentWhatsAppButtonProps {
  universityName: string;
  /** Path to the university calendar page with filter, e.g. "/kalender-ui-2026?filter=new_student_snbp" */
  calendarPath: string;
  lang: string;
  nextDeadline?: { phaseLocal: string; phase: string; dateStart: string; dateEnd: string } | null;
}

export function ParentWhatsAppButton({ universityName, calendarPath, lang, nextDeadline }: ParentWhatsAppButtonProps) {
  const isId = lang === 'id';

  const handleClick = () => {
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
    const fullUrl = appendReferralParam(baseUrl + calendarPath);

    const deadlineInfo = nextDeadline
      ? `\n📌 ${isId ? 'Tenggat berikutnya' : 'Next deadline'}: ${isId ? nextDeadline.phaseLocal : nextDeadline.phase}`
      : '';

    const text = isId
      ? `Ini jadwal daftar ulang anak kita di ${universityName} 🎓${deadlineInfo}\n\nLihat jadwal lengkap: ${fullUrl}`
      : `Here's our child's enrollment schedule at ${universityName} 🎓${deadlineInfo}\n\nSee full schedule: ${fullUrl}`;

    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    window._paq?.push(['trackEvent', 'Share', 'WhatsApp-Parent-Uni', universityName]);
  };

  return (
    <button
      onClick={handleClick}
      className="inline-flex items-center gap-1 text-xs text-semantic-success hover:underline"
      aria-label={isId ? `Kirim jadwal ${universityName} ke orang tua` : `Share ${universityName} schedule with parent`}
    >
      <svg className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
      </svg>
      {isId ? 'Kirim ke Ortu' : 'Send to Parent'}
    </button>
  );
}
